import { createTheme } from '@mui/material/styles';

// ---- Brand design tokens ---------------------------------------------------
export const brand = {
  navy: '#0A2A66',
  blue: '#1565C0',
  blueLight: '#4F8FE0',
  accent: '#FF7A45',
  teal: '#00897B',
  indigo: '#5C6BC0',
};

export const gradients = {
  hero: 'linear-gradient(135deg, #0A2A66 0%, #0D47A1 45%, #1565C0 100%)',
  primary: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
  accent: 'linear-gradient(135deg, #FF7A45 0%, #F4511E 100%)',
  subtle: 'linear-gradient(180deg, rgba(21,101,192,0.05) 0%, rgba(21,101,192,0) 100%)',
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#1565C0',
      light: '#4F8FE0',
      dark: '#0A2A66',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#37474F',
      light: '#546E7A',
      dark: '#1B2A33',
      contrastText: '#ffffff',
    },
    accent: {
      main: '#FF7A45',
      light: '#FF9A6E',
      dark: '#F4511E',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F6F8FB',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#16202A',
      secondary: '#5A6B7B',
    },
    divider: 'rgba(16, 32, 50, 0.08)',
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
      fontSize: '3.25rem',
      fontWeight: 800,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
      '@media (max-width:600px)': { fontSize: '2.1rem' },
    },
    h2: {
      fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
      fontSize: '2.5rem',
      fontWeight: 800,
      lineHeight: 1.18,
      letterSpacing: '-0.02em',
      '@media (max-width:600px)': { fontSize: '1.8rem' },
    },
    h3: {
      fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
      '@media (max-width:600px)': { fontSize: '1.5rem' },
    },
    h4: {
      fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: 1.4,
    },
    h6: {
      fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle1: { fontWeight: 600 },
    button: { fontWeight: 600, letterSpacing: '0.01em' },
    body1: { fontSize: '1rem', lineHeight: 1.75 },
    body2: { fontSize: '0.9rem', lineHeight: 1.7 },
    overline: {
      fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
      fontWeight: 700,
      letterSpacing: '0.18em',
      fontSize: '0.75rem',
    },
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          padding: '11px 26px',
          borderRadius: 12,
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
          boxShadow: '0 10px 24px -8px rgba(13, 71, 161, 0.55)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1976D2 0%, #0A2A66 100%)',
            boxShadow: '0 14px 30px -8px rgba(13, 71, 161, 0.6)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': { borderWidth: 2 },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 10px 40px -16px rgba(16, 32, 50, 0.16)',
          borderRadius: 18,
          border: '1px solid rgba(16, 32, 50, 0.06)',
          transition: 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 28px 60px -24px rgba(13, 71, 161, 0.35)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 18 },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { boxShadow: 'none' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#fff',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (min-width:1200px)': { maxWidth: 1200 },
        },
      },
    },
  },
});

export default theme;
